/* =========================================================================
   Shapes — the pan/zoom canvas every candidate is rendered into.

   Self-contained: it borrows the shipped canvas's interaction model (drag to
   pan, wheel to zoom at the cursor) but imports nothing from DevelopmentPlan,
   so nothing here can affect the real skill tree. Card geometry is pinned to
   the same 200x110 footprint as the shipped node so the comparison is honest.
   ========================================================================= */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PlusOutlined,
  MinusOutlined,
  CompressOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

import {
  CARD_H,
  CARD_W,
  CORE_ID,
  ShapeEdge,
  ShapeNode,
  ShapeTrack,
} from './mock';
import { Candidate, measure } from './layouts';
import { octiPath, orthoPath, straightPath } from './edgePaths';

type Props = {
  candidate: Candidate;
  nodes: ShapeNode[];
  tracks: ShapeTrack[];
  edges: ShapeEdge[];
};

const ZOOM_MIN = 0.05;
const ZOOM_MAX = 1.8;
const WORLD_MARGIN = 240;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

const rgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map(c => `${c}${c}`)
          .join('')
      : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ShapeCanvas: React.FC<Props> = ({ candidate, nodes, tracks, edges }) => {
  const vpRef = useRef<HTMLDivElement>(null);
  const [tf, setTf] = useState({ tx: 0, ty: 0, zoom: 0.4 });
  const [focusTrack, setFocusTrack] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ moved: boolean; x: number; y: number } | null>(null);
  const [vp, setVp] = useState({ w: 1200, h: 700 });

  const positions = useMemo(
    () => candidate.layout(nodes, tracks),
    [candidate, nodes, tracks],
  );

  const metrics = useMemo(
    () => measure(positions, vp.w, vp.h, WORLD_MARGIN),
    [positions, vp.w, vp.h],
  );

  /**
   * The world is sized to the candidate's true bounds (not a symmetric square),
   * and `ox/oy` translate layout space into world space. A node at layout {x,y}
   * renders at {ox + x, oy + y}.
   */
  const world = useMemo(
    () => ({
      w: metrics.worldW,
      h: metrics.worldH,
      ox: -metrics.minX,
      oy: -metrics.minY,
    }),
    [metrics.worldW, metrics.worldH, metrics.minX, metrics.minY],
  );

  const trackColor = useMemo(() => {
    const map: Record<string, string> = {};
    tracks.forEach(t => {
      map[t.code] = t.color;
    });
    return map;
  }, [tracks]);

  const byCode = useMemo(() => {
    const map: Record<string, ShapeNode> = {};
    nodes.forEach(n => {
      map[n.code] = n;
    });
    return map;
  }, [nodes]);

  const fit = useCallback(() => {
    const el = vpRef.current;
    if (!el || !el.clientWidth) return;
    const zoom = clamp(
      Math.min(el.clientWidth / world.w, el.clientHeight / world.h),
      ZOOM_MIN,
      ZOOM_MAX,
    );
    setTf({
      zoom,
      tx: (el.clientWidth - world.w * zoom) / 2,
      ty: (el.clientHeight - world.h * zoom) / 2,
    });
  }, [world.w, world.h]);

  // Track viewport size (drives the fit-zoom metric) and fit once it is real.
  useEffect(() => {
    const el = vpRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth > 0 && el.clientHeight > 0) {
        setVp({ w: el.clientWidth, h: el.clientHeight });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-fit whenever the candidate (and therefore the world) changes.
  useEffect(() => {
    const id = window.setTimeout(() => fit(), 0);
    return () => window.clearTimeout(id);
  }, [fit, candidate.key]);

  useEffect(() => {
    const el = vpRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setTf(prev => {
        const zoom = clamp(
          prev.zoom * Math.exp(-e.deltaY * 0.0015),
          ZOOM_MIN,
          ZOOM_MAX,
        );
        const k = zoom / prev.zoom;
        return {
          zoom,
          tx: cx - (cx - prev.tx) * k,
          ty: cy - (cy - prev.ty) * k,
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!drag.current) return;
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) drag.current.moved = true;
      drag.current.x = e.clientX;
      drag.current.y = e.clientY;
      setTf(prev => ({ ...prev, tx: prev.tx + dx, ty: prev.ty + dy }));
    };
    const onUp = () => {
      drag.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const zoomBy = (factor: number) => {
    const el = vpRef.current;
    if (!el) return;
    const cx = el.clientWidth / 2;
    const cy = el.clientHeight / 2;
    setTf(prev => {
      const zoom = clamp(prev.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const k = zoom / prev.zoom;
      return { zoom, tx: cx - (cx - prev.tx) * k, ty: cy - (cy - prev.ty) * k };
    });
  };

  const pathFor =
    candidate.edgeStyle === 'ortho'
      ? orthoPath
      : candidate.edgeStyle === 'octi'
        ? octiPath
        : straightPath;

  return (
    <div className="shapes-stage">
      <div
        ref={vpRef}
        className={`shapes-canvas${dragging ? ' is-dragging' : ''}`}
        onMouseDown={e => {
          if (e.button !== 0) return;
          drag.current = { moved: false, x: e.clientX, y: e.clientY };
          setDragging(true);
        }}
      >
        <div
          className="shapes-world"
          style={{
            width: world.w,
            height: world.h,
            transform: `translate(${tf.tx}px, ${tf.ty}px) scale(${tf.zoom})`,
          }}
        >
          <svg
            className="shapes-edges"
            width={world.w}
            height={world.h}
            viewBox={`0 0 ${world.w} ${world.h}`}
          >
            {edges.map(edge => {
              const a = positions[edge.from];
              const b = positions[edge.to];
              if (!a || !b) return null;

              const sTrack =
                edge.from === CORE_ID ? null : byCode[edge.from]?.track_code;
              const tTrack =
                edge.to === CORE_ID ? null : byCode[edge.to]?.track_code;
              const color = tTrack ? trackColor[tTrack] : '#9fb0c8';
              const faded =
                !!focusTrack && sTrack !== focusTrack && tTrack !== focusTrack;

              // Edge line thickness. These are SCREEN pixels (constant at any
              // zoom, via vectorEffect="non-scaling-stroke" on the <path>).
              // Bump these numbers to make the routes bolder.
              let stroke = rgba(color, 0.7);
              let width = 5; // same-track prereq — the bold rails
              if (faded) {
                stroke = rgba(color, 0.06);
                width = 1;
              } else if (edge.soft) {
                stroke = rgba(color, 0.4);
                width = 2; // soft cross-track recommendation
              } else if (edge.crossTrack) {
                stroke = rgba(color, 0.5);
                width = 2.2; // hard cross-track prereq
              }

              return (
                <path
                  key={`${edge.from}->${edge.to}${edge.soft ? ':soft' : ''}`}
                  d={pathFor(a, b, world.ox, world.oy)}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={width}
                  strokeDasharray={
                    edge.soft ? '2 8' : edge.crossTrack ? '6 6' : undefined
                  }
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </svg>

          <div
            className="shapes-core"
            style={{
              left: world.ox + (positions[CORE_ID]?.x ?? 0),
              top: world.oy + (positions[CORE_ID]?.y ?? 0),
            }}
          >
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <path d="M50 7 C51 40 60 49 93 50 C60 51 51 60 50 93 C49 60 40 51 7 50 C40 49 49 40 50 7 Z" />
            </svg>
          </div>

          {nodes.map(node => {
            const p = positions[node.code];
            if (!p) return null;
            const faded = !!focusTrack && node.track_code !== focusTrack;
            return (
              <div
                key={node.code}
                className={`shapes-node-pos${faded ? ' is-faded' : ''}`}
                style={{ left: world.ox + p.x, top: world.oy + p.y }}
              >
                <div
                  className="shapes-node"
                  style={{
                    ['--node-color' as string]: trackColor[node.track_code],
                  }}
                >
                  <div className="shapes-node__head">
                    <span className="shapes-node__tier">{node.tier}</span>
                    <span className="shapes-node__code">{node.code}</span>
                  </div>
                  <div className="shapes-node__name">{node.name}</div>
                  <div className="shapes-node__foot">
                    <span className="shapes-node__pips" aria-hidden>
                      {Array.from({ length: node.maxRank }).map((_, i) => (
                        <span key={i} className="shapes-pip" />
                      ))}
                    </span>
                    {node.games > 0 && (
                      <span className="shapes-node__scen">
                        <ExperimentOutlined /> {node.games}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="shapes-legend">
          <div className="shapes-legend__title">Tracks · {tracks.length}</div>
          <div className="shapes-legend__list">
            {tracks.map(t => (
              <button
                type="button"
                key={t.code}
                className={`shapes-legend__item${
                  focusTrack === t.code ? ' is-active' : ''
                }`}
                style={{ ['--track-color' as string]: t.color }}
                onClick={() =>
                  setFocusTrack(focusTrack === t.code ? null : t.code)
                }
              >
                <span className="shapes-legend__dot" />
                <span className="shapes-legend__name">{t.name}</span>
                <span className="shapes-legend__count">
                  {nodes.filter(n => n.track_code === t.code).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="shapes-zoomctl">
          <button type="button" onClick={() => zoomBy(1.2)} title="Zoom in">
            <PlusOutlined />
          </button>
          <button
            type="button"
            onClick={() => zoomBy(1 / 1.2)}
            title="Zoom out"
          >
            <MinusOutlined />
          </button>
          <button type="button" onClick={() => fit()} title="Fit">
            <CompressOutlined />
          </button>
        </div>

        <div className="shapes-hint">
          Drag to pan · scroll to zoom · click a track to isolate it
        </div>
      </div>

      <div className="shapes-metrics">
        <Metric label="Nodes" value={String(metrics.nodes)} />
        <Metric
          label="Card overlaps"
          value={String(metrics.overlaps)}
          tone={metrics.overlaps === 0 ? 'good' : 'bad'}
          note={
            metrics.tightest
              ? `tightest Δx ${metrics.tightest.dx} Δy ${metrics.tightest.dy} (need Δx≥${CARD_W} or Δy≥${CARD_H})`
              : 'no two cards share space'
          }
        />
        <Metric
          label="World"
          value={`${metrics.worldW} × ${metrics.worldH}`}
          note="px needed to hold the tree"
        />
        <Metric
          label="Fit zoom"
          value={metrics.fitZoom.toFixed(2)}
          tone={metrics.fitZoom >= 0.2 ? 'good' : 'bad'}
          note={
            metrics.fitZoom >= 0.2
              ? 'readable inside the shipped 0.2 zoom floor'
              : 'below the shipped 0.2 zoom floor — cards unreadable when fully zoomed out'
          }
        />
      </div>
    </div>
  );
};

const Metric: React.FC<{
  label: string;
  value: string;
  note?: string;
  tone?: 'good' | 'bad';
}> = ({ label, value, note, tone }) => (
  <div className="shapes-metric">
    <div className="shapes-metric__label">{label}</div>
    <div className={`shapes-metric__value${tone ? ` is-${tone}` : ''}`}>
      {value}
    </div>
    {note && <div className="shapes-metric__note">{note}</div>}
  </div>
);

export default ShapeCanvas;
